import { ResoPropertySubType, ResoPropertyType, StandardStatus } from '@zoocasa/go-search';
import { LOCALE_COMMON_PATHS, LocalePaths } from './locales';

export type PropertyType = typeof ResoPropertyType[keyof typeof ResoPropertyType] | null;
export type PropertySubType = typeof ResoPropertySubType[keyof typeof ResoPropertySubType] | null;


//#region helper functions
const rentalPropertyTypes: PropertyType[] = [
  ResoPropertyType.CommercialLease,
  ResoPropertyType.ResidentialLease,
  ResoPropertyType.OtherLease,
];

/**
 * Check if a property type is a rental property type.
 * @param propertyType The property type to check.
 * @returns `true` if the property type is a rental property type, `false` otherwise.
 */
export function isRental(propertyType: PropertyType) {
  return rentalPropertyTypes.includes(propertyType);
}

const soldStatuses = [
  StandardStatus.STANDARD_STATUS_CLOSED,
  StandardStatus.STANDARD_STATUS_HOLD,
  StandardStatus.STANDARD_STATUS_WITHDRAWN,
  StandardStatus.STANDARD_STATUS_PENDING,
];

/**
 * Check if a status is a sold status.
 * @remarks
 * - {@link StandardStatus.STANDARD_STATUS_CLOSED}, {@link StandardStatus.STANDARD_STATUS_HOLD}, {@link StandardStatus.STANDARD_STATUS_WITHDRAWN}, and {@link StandardStatus.STANDARD_STATUS_PENDING} statuses are considered sold.
 * @param status The status to check.
 * @returns `true` if the status is a sold status, `false` otherwise.
 */
export function isSold(status: StandardStatus) {
  return soldStatuses.includes(status);
}

export function isPending(status: StandardStatus) {
  return status === StandardStatus.STANDARD_STATUS_PENDING;
}

/**
 * Check if a status is an expired status.
 * @remarks
 * - {@link StandardStatus.STANDARD_STATUS_EXPIRED} status is considered expired.
 * @param status The status to check.
 * @returns `true` if the status is an expired status, `false` otherwise.
 */
export function isExpired(status: StandardStatus) {
  return status === StandardStatus.STANDARD_STATUS_EXPIRED;
}

/**
 * Check if a status is a deleted status.
 * @remarks
 * - {@link StandardStatus.STANDARD_STATUS_DELETE} status is considered deleted.
 * @param status The status to check.
 * @returns `true` if the status is a deleted status, `false` otherwise.
 */
export function isDeleted(status: StandardStatus) {
  return status === StandardStatus.STANDARD_STATUS_DELETE;
}

/**
 * Check if a status is an active status.
 * @remarks
 * - {@link StandardStatus.STANDARD_STATUS_ACTIVE} and {@link StandardStatus.STANDARD_STATUS_ACTIVE_UNDER_CONTRACT} statuses are considered active.
 * @param status The status to check.
 * @returns `true` if the status is an active status, `false` otherwise.
 */
export function isActive(status: StandardStatus) {
  return status === StandardStatus.STANDARD_STATUS_ACTIVE || status === StandardStatus.STANDARD_STATUS_ACTIVE_UNDER_CONTRACT;
}

/**
 * Check if a status is a coming soon status.
 * @remarks
 * - {@link StandardStatus.STANDARD_STATUS_COMING_SOON} status is considered coming soon.
 * @param status The status to check.
 * @returns `true` if the status is a coming soon status, `false` otherwise.
 */
export function isComingSoon(status: StandardStatus) {
  return status === StandardStatus.STANDARD_STATUS_COMING_SOON;
}

/**
 * Check if a status is a canceled status.
 * @remarks
 * - {@link StandardStatus.STANDARD_STATUS_CANCELED} status is considered canceled.
 * @param status The status to check.
 * @returns `true` if the status is a canceled status, `false` otherwise.
 */
export function isCanceled(status: StandardStatus) {
  return status === StandardStatus.STANDARD_STATUS_CANCELED;
}

/**
 * Check if a status is an unsupported status.
 * @remarks
 * - {@link StandardStatus.UNRECOGNIZED}, {@link StandardStatus.STANDARD_STATUS_UNSPECIFIED}, and {@link StandardStatus.STANDARD_STATUS_INCOMPLETE} statuses are considered unsupported.
 * @param status The status to check.
 * @returns `true` if the status is an unsupported status, `false` otherwise.
 */
export function isUnsupportedStatus(status: StandardStatus) {
  return status === StandardStatus.UNRECOGNIZED || status === StandardStatus.STANDARD_STATUS_UNSPECIFIED || status === StandardStatus.STANDARD_STATUS_INCOMPLETE;
}

/**
 * Check if a status is an expired or canceled status.
 * @remarks
 * - {@link StandardStatus.STANDARD_STATUS_EXPIRED} and {@link StandardStatus.STANDARD_STATUS_CANCELED} statuses are considered expired or canceled.
 * - {@link isExpired} and {@link isCanceled} functions are used to check if the status is expired or canceled.
 * @param status The status to check.
 * @returns `true` if the status is an expired or canceled status, `false` otherwise.
 */
export function isExpiredOrCanceled(status: StandardStatus) {
  return isExpired(status) || isCanceled(status);
}


/**
 * Convert a string to a {@link StandardStatus}.
 *
 * @param status The status to convert.
 * @returns The {@link StandardStatus} enum value or {@link StandardStatus.UNRECOGNIZED} if the status is not recognized.
 */
export function standardStatusFromJSON(status: string): StandardStatus {
  switch (status?.toLowerCase()) {
    case 'unspecified':
      return StandardStatus.STANDARD_STATUS_UNSPECIFIED;
    case 'active':
      return StandardStatus.STANDARD_STATUS_ACTIVE;
    case 'active under contract':
      return StandardStatus.STANDARD_STATUS_ACTIVE_UNDER_CONTRACT;
    case 'canceled':
      return StandardStatus.STANDARD_STATUS_CANCELED;
    case 'closed':
      return StandardStatus.STANDARD_STATUS_CLOSED;
    case 'coming soon':
      return StandardStatus.STANDARD_STATUS_COMING_SOON;
    case 'delete':
      return StandardStatus.STANDARD_STATUS_DELETE;
    case 'expired':
      return StandardStatus.STANDARD_STATUS_EXPIRED;
    case 'hold':
      return StandardStatus.STANDARD_STATUS_HOLD;
    case 'incomplete':
      return StandardStatus.STANDARD_STATUS_INCOMPLETE;
    case 'pending':
      return StandardStatus.STANDARD_STATUS_PENDING;
    case 'withdrawn':
      return StandardStatus.STANDARD_STATUS_WITHDRAWN;
    default:
      return StandardStatus.UNRECOGNIZED;
  }
}

/**
 * Maps a listing status to the appropriate localization key for display text.
 *
 * This function determines the correct translation key based on the listing status
 * and whether the listing is for rental or sale. It handles different status types
 * and provides appropriate labels for both rental and sale contexts.
 *
 * @param status The standard status of the listing from the StandardStatus enum
 * @param isRental Boolean indicating if the listing is for rental (true) or sale (false), defaults to false
 * @returns A LocalePaths key that corresponds to the appropriate translation string
 *
 * @example
 * ```tsx
 * const labelKey = getStatusLabelKey(StandardStatus.STANDARD_STATUS_ACTIVE, true);
 * // Returns: 'common:standard_status.active_rent' (translates to "For Rent")
 *
 * const labelKey = getStatusLabelKey(StandardStatus.STANDARD_STATUS_ACTIVE, false);
 * // Returns: 'common:standard_status.active_sale' (translates to "For Sale")
 * ```
 *
 * @remarks
 * - Active listings show different text for rental vs sale
 * - Closed listings show different text for rental vs sale
 * - Most other statuses use the same text regardless of rental/sale context
 * - Falls back to 'other' status if an unrecognized status is provided
 */
export function getStatusLabelKey(status: StandardStatus, isRental: boolean = false) {
  let statusLabelKey: LocalePaths;
  switch (status) {
    case StandardStatus.STANDARD_STATUS_ACTIVE:
      statusLabelKey = isRental ? LOCALE_COMMON_PATHS.STANDARD_STATUS_ACTIVE_RENT : LOCALE_COMMON_PATHS.STANDARD_STATUS_ACTIVE_SALE;
      break;
    case StandardStatus.STANDARD_STATUS_CANCELED:
      statusLabelKey = LOCALE_COMMON_PATHS.STANDARD_STATUS_CANCELED;
      break;
    case StandardStatus.STANDARD_STATUS_CLOSED:
      statusLabelKey = isRental ? LOCALE_COMMON_PATHS.STANDARD_STATUS_CLOSED_RENT : LOCALE_COMMON_PATHS.STANDARD_STATUS_CLOSED_SALE;
      break;
    case StandardStatus.STANDARD_STATUS_COMING_SOON:
      statusLabelKey = LOCALE_COMMON_PATHS.STANDARD_STATUS_COMING_SOON;
      break;
    case StandardStatus.STANDARD_STATUS_DELETE:
      statusLabelKey = LOCALE_COMMON_PATHS.STANDARD_STATUS_DELETE;
      break;
    case StandardStatus.STANDARD_STATUS_EXPIRED:
      statusLabelKey = LOCALE_COMMON_PATHS.STANDARD_STATUS_EXPIRED;
      break;
    case StandardStatus.STANDARD_STATUS_HOLD:
      statusLabelKey = LOCALE_COMMON_PATHS.STANDARD_STATUS_HOLD;
      break;
    case StandardStatus.STANDARD_STATUS_INCOMPLETE:
      statusLabelKey = LOCALE_COMMON_PATHS.STANDARD_STATUS_INCOMPLETE;
      break;
    case StandardStatus.STANDARD_STATUS_PENDING:
      statusLabelKey = LOCALE_COMMON_PATHS.STANDARD_STATUS_PENDING;
      break;
    case StandardStatus.STANDARD_STATUS_WITHDRAWN:
      statusLabelKey = LOCALE_COMMON_PATHS.STANDARD_STATUS_WITHDRAWN;
      break;
    default:
      statusLabelKey = LOCALE_COMMON_PATHS.STANDARD_STATUS_OTHER;
      break;
  }
  return statusLabelKey;
}

export function getPropertyTypeLabelKey(propertyType: PropertyType) {
  switch (propertyType) {
    case ResoPropertyType.Residential:
      return LOCALE_COMMON_PATHS.PROPERTY_TYPE_RESIDENTIAL;
    case ResoPropertyType.Land:
      return LOCALE_COMMON_PATHS.PROPERTY_TYPE_LAND;
    case ResoPropertyType.BusinessOpportunity:
      return LOCALE_COMMON_PATHS.PROPERTY_TYPE_BUSINESS_OPPORTUNITY;
    case ResoPropertyType.CommercialLease:
      return LOCALE_COMMON_PATHS.PROPERTY_TYPE_COMMERCIAL_LEASE;
    case ResoPropertyType.CommercialSale:
      return LOCALE_COMMON_PATHS.PROPERTY_TYPE_COMMERCIAL_SALE;
    case ResoPropertyType.ManufacturedInPark:
      return LOCALE_COMMON_PATHS.PROPERTY_TYPE_MANUFACTURED_IN_PARK;
    case ResoPropertyType.ResidentialIncome:
      return LOCALE_COMMON_PATHS.PROPERTY_TYPE_RESIDENTIAL_INCOME;
    case ResoPropertyType.ResidentialLease:
      return LOCALE_COMMON_PATHS.PROPERTY_TYPE_RESIDENTIAL_LEASE;
    case ResoPropertyType.OtherLease:
      return LOCALE_COMMON_PATHS.PROPERTY_TYPE_OTHER_LEASE;
    case ResoPropertyType.Other:
      return LOCALE_COMMON_PATHS.PROPERTY_TYPE_OTHER;
    case ResoPropertyType.Farm:
      return LOCALE_COMMON_PATHS.PROPERTY_TYPE_FARM;
    default:
      return propertyType;
  }
}

export function getPropertySubTypeLabelKey(propertySubType: PropertySubType) {
  switch (propertySubType) {
    case ResoPropertySubType.Agriculture:
      return LOCALE_COMMON_PATHS.PROPERTY_SUB_TYPE_AGRICULTURE;
    case ResoPropertySubType.Apartment:
      return LOCALE_COMMON_PATHS.PROPERTY_SUB_TYPE_APARTMENT;
    case ResoPropertySubType.Business:
      return LOCALE_COMMON_PATHS.PROPERTY_SUB_TYPE_BUSINESS;
    case ResoPropertySubType.Cabin:
      return LOCALE_COMMON_PATHS.PROPERTY_SUB_TYPE_CABIN;
    case ResoPropertySubType.Condominium:
      return LOCALE_COMMON_PATHS.PROPERTY_SUB_TYPE_CONDOMINIUM;
    case ResoPropertySubType.Duplex:
      return LOCALE_COMMON_PATHS.PROPERTY_SUB_TYPE_DUPLEX;
    case ResoPropertySubType.Industrial:
      return LOCALE_COMMON_PATHS.PROPERTY_SUB_TYPE_INDUSTRIAL;
    case ResoPropertySubType.Office:
      return LOCALE_COMMON_PATHS.PROPERTY_SUB_TYPE_OFFICE;
    case ResoPropertySubType.Quadruplex:
      return LOCALE_COMMON_PATHS.PROPERTY_SUB_TYPE_QUADRUPLEX;
    case ResoPropertySubType.Ranch:
      return LOCALE_COMMON_PATHS.PROPERTY_SUB_TYPE_RANCH;
    case ResoPropertySubType.Retail:
      return LOCALE_COMMON_PATHS.PROPERTY_SUB_TYPE_RETAIL;
    case ResoPropertySubType.Timeshare:
      return LOCALE_COMMON_PATHS.PROPERTY_SUB_TYPE_TIMESHARE;
    case ResoPropertySubType.Townhouse:
      return LOCALE_COMMON_PATHS.PROPERTY_SUB_TYPE_TOWNHOUSE;
    case ResoPropertySubType.Triplex:
      return LOCALE_COMMON_PATHS.PROPERTY_SUB_TYPE_TRIPLEX;
    case ResoPropertySubType.Warehouse:
      return LOCALE_COMMON_PATHS.PROPERTY_SUB_TYPE_WAREHOUSE;
    case ResoPropertySubType.BoatSlip:
      return LOCALE_COMMON_PATHS.PROPERTY_SUB_TYPE_BOAT_SLIP;
    case ResoPropertySubType.CoOwnership:
      return LOCALE_COMMON_PATHS.PROPERTY_SUB_TYPE_CO_OWNERSHIP;
    case ResoPropertySubType.DeededParking:
      return LOCALE_COMMON_PATHS.PROPERTY_SUB_TYPE_DEEDED_PARKING;
    case ResoPropertySubType.HotelMotel:
      return LOCALE_COMMON_PATHS.PROPERTY_SUB_TYPE_HOTEL_MOTEL;
    case ResoPropertySubType.ManufacturedHome:
      return LOCALE_COMMON_PATHS.PROPERTY_SUB_TYPE_MANUFACTURED_HOME;
    case ResoPropertySubType.ManufacturedOnLand:
      return LOCALE_COMMON_PATHS.PROPERTY_SUB_TYPE_MANUFACTURED_ON_LAND;
    case ResoPropertySubType.MixedUse:
      return LOCALE_COMMON_PATHS.PROPERTY_SUB_TYPE_MIXED_USE;
    case ResoPropertySubType.MobileHome:
      return LOCALE_COMMON_PATHS.PROPERTY_SUB_TYPE_MOBILE_HOME;
    case ResoPropertySubType.MobileHomePark:
      return LOCALE_COMMON_PATHS.PROPERTY_SUB_TYPE_MOBILE_HOME_PARK;
    case ResoPropertySubType.MultiFamily:
      return LOCALE_COMMON_PATHS.PROPERTY_SUB_TYPE_MULTI_FAMILY;
    case ResoPropertySubType.OwnYourOwn:
      return LOCALE_COMMON_PATHS.PROPERTY_SUB_TYPE_OWN_YOUR_OWN;
    case ResoPropertySubType.SingleFamilyResidence:
      return LOCALE_COMMON_PATHS.PROPERTY_SUB_TYPE_SINGLE_FAMILY_RESIDENCE;
    case ResoPropertySubType.StockCooperative:
      return LOCALE_COMMON_PATHS.PROPERTY_SUB_TYPE_STOCK_COOPERATIVE;
    case ResoPropertySubType.TenancyInCommon:
      return LOCALE_COMMON_PATHS.PROPERTY_SUB_TYPE_TENANCY_IN_COMMON;
    case ResoPropertySubType.UnimprovedLand:
      return LOCALE_COMMON_PATHS.PROPERTY_SUB_TYPE_UNIMPROVED_LAND;
    case ResoPropertySubType.ResidentialLand:
      return LOCALE_COMMON_PATHS.PROPERTY_SUB_TYPE_RESIDENTIAL_LAND;
    case ResoPropertySubType.CommercialLand:
      return LOCALE_COMMON_PATHS.PROPERTY_SUB_TYPE_COMMERCIAL_LAND;
    case ResoPropertySubType.Apartment_Condominium:
      return LOCALE_COMMON_PATHS.PROPERTY_SUB_TYPE_APARTMENT_CONDOMINIUM;
    case ResoPropertySubType.HotelMotelESP:
      return LOCALE_COMMON_PATHS.PROPERTY_SUB_TYPE_HOTEL_MOTEL_EXP;
    case ResoPropertySubType.OtherSubType:
      return LOCALE_COMMON_PATHS.PROPERTY_SUB_TYPE_OTHER;
    case ResoPropertySubType.FarmSubType:
      return LOCALE_COMMON_PATHS.PROPERTY_SUB_TYPE_FARM;
    default:
      return propertySubType;
  }
}

//#endregion

//#region Room Type related constants and helper functions

export const ROOMTYPE_BASEMENT = 'Basement' as const;
export const ROOMTYPE_BATHROOM_1 = 'Bathroom 1' as const;
export const ROOMTYPE_BATHROOM_2 = 'Bathroom 2' as const;
export const ROOMTYPE_BATHROOM_3 = 'Bathroom 3' as const;
export const ROOMTYPE_BATHROOM_4 = 'Bathroom 4' as const;
export const ROOMTYPE_BATHROOM_5 = 'Bathroom 5' as const;
export const ROOMTYPE_BATHROOM = 'Bathroom' as const;
export const ROOMTYPE_BEDROOM_1 = 'Bedroom 1' as const;
export const ROOMTYPE_BEDROOM_2 = 'Bedroom 2' as const;
export const ROOMTYPE_BEDROOM_3 = 'Bedroom 3' as const;
export const ROOMTYPE_BEDROOM_4 = 'Bedroom 4' as const;
export const ROOMTYPE_BEDROOM_5 = 'Bedroom 5' as const;
export const ROOMTYPE_BEDROOM = 'Bedroom' as const;
export const ROOMTYPE_BONUS_ROOM = 'Bonus Room' as const;
export const ROOMTYPE_DEN = 'Den' as const;
export const ROOMTYPE_DINING_ROOM = 'Dining Room' as const;
export const ROOMTYPE_EXERCISE_ROOM = 'Exercise Room' as const;
export const ROOMTYPE_FAMILY_ROOM = 'Family Room' as const;
export const ROOMTYPE_GAME_ROOM = 'Game Room' as const;
export const ROOMTYPE_GREAT_ROOM = 'Great Room' as const;
export const ROOMTYPE_GYM = 'Gym' as const;
export const ROOMTYPE_KITCHEN = 'Kitchen' as const;
export const ROOMTYPE_LAUNDRY = 'Laundry' as const;
export const ROOMTYPE_LIBRARY = 'Library' as const;
export const ROOMTYPE_LIVING_ROOM = 'Living Room' as const;
export const ROOMTYPE_LOFT = 'Loft' as const;
export const ROOMTYPE_MASTER_BATHROOM = 'Master Bathroom' as const;
export const ROOMTYPE_MASTER_BEDROOM = 'Master Bedroom' as const;
export const ROOMTYPE_MEDIA_ROOM = 'Media Room' as const;
export const ROOMTYPE_OFFICE = 'Office' as const;
export const ROOMTYPE_SAUNA = 'Sauna' as const;
export const ROOMTYPE_UTILITY_ROOM = 'Utility Room' as const;
export const ROOMTYPE_WORKSHOP = 'Workshop' as const;

export type RoomType = typeof ROOMTYPE_BASEMENT | typeof ROOMTYPE_BATHROOM_1 | typeof ROOMTYPE_BATHROOM_2 | typeof ROOMTYPE_BATHROOM_3 | typeof ROOMTYPE_BATHROOM_4 | typeof ROOMTYPE_BATHROOM_5 | typeof ROOMTYPE_BATHROOM | typeof ROOMTYPE_BEDROOM_1 | typeof ROOMTYPE_BEDROOM_2 | typeof ROOMTYPE_BEDROOM_3 | typeof ROOMTYPE_BEDROOM_4 | typeof ROOMTYPE_BEDROOM_5 | typeof ROOMTYPE_BEDROOM | typeof ROOMTYPE_BONUS_ROOM | typeof ROOMTYPE_DEN | typeof ROOMTYPE_DINING_ROOM | typeof ROOMTYPE_EXERCISE_ROOM | typeof ROOMTYPE_FAMILY_ROOM | typeof ROOMTYPE_GAME_ROOM | typeof ROOMTYPE_GREAT_ROOM | typeof ROOMTYPE_GYM | typeof ROOMTYPE_KITCHEN | typeof ROOMTYPE_LAUNDRY | typeof ROOMTYPE_LIBRARY | typeof ROOMTYPE_LIVING_ROOM | typeof ROOMTYPE_LOFT | typeof ROOMTYPE_MASTER_BATHROOM | typeof ROOMTYPE_MASTER_BEDROOM | typeof ROOMTYPE_MEDIA_ROOM | typeof ROOMTYPE_OFFICE | typeof ROOMTYPE_SAUNA | typeof ROOMTYPE_UTILITY_ROOM | typeof ROOMTYPE_WORKSHOP;

export function getRoomTypeLabelKey(roomType: RoomType) {
  switch (roomType) {
    case ROOMTYPE_BASEMENT:
      return LOCALE_COMMON_PATHS.ROOM_TYPE_BASEMENT;
    case ROOMTYPE_BATHROOM_1:
      return LOCALE_COMMON_PATHS.ROOM_TYPE_BATHROOM_1;
    case ROOMTYPE_BATHROOM_2:
      return LOCALE_COMMON_PATHS.ROOM_TYPE_BATHROOM_2;
    case ROOMTYPE_BATHROOM_3:
      return LOCALE_COMMON_PATHS.ROOM_TYPE_BATHROOM_3;
    case ROOMTYPE_BATHROOM_4:
      return LOCALE_COMMON_PATHS.ROOM_TYPE_BATHROOM_4;
    case ROOMTYPE_BATHROOM_5:
      return LOCALE_COMMON_PATHS.ROOM_TYPE_BATHROOM_5;
    case ROOMTYPE_BATHROOM:
      return LOCALE_COMMON_PATHS.ROOM_TYPE_BATHROOM;
    case ROOMTYPE_BEDROOM_1:
      return LOCALE_COMMON_PATHS.ROOM_TYPE_BEDROOM_1;
    case ROOMTYPE_BEDROOM_2:
      return LOCALE_COMMON_PATHS.ROOM_TYPE_BEDROOM_2;
    case ROOMTYPE_BEDROOM_3:
      return LOCALE_COMMON_PATHS.ROOM_TYPE_BEDROOM_3;
    case ROOMTYPE_BEDROOM_4:
      return LOCALE_COMMON_PATHS.ROOM_TYPE_BEDROOM_4;
    case ROOMTYPE_BEDROOM_5:
      return LOCALE_COMMON_PATHS.ROOM_TYPE_BEDROOM_5;
    case ROOMTYPE_BEDROOM:
      return LOCALE_COMMON_PATHS.ROOM_TYPE_BEDROOM;
    case ROOMTYPE_BONUS_ROOM:
      return LOCALE_COMMON_PATHS.ROOM_TYPE_BONUS_ROOM;
    case ROOMTYPE_DEN:
      return LOCALE_COMMON_PATHS.ROOM_TYPE_DEN;
    case ROOMTYPE_DINING_ROOM:
      return LOCALE_COMMON_PATHS.ROOM_TYPE_DINING_ROOM;
    case ROOMTYPE_EXERCISE_ROOM:
      return LOCALE_COMMON_PATHS.ROOM_TYPE_EXERCISE_ROOM;
    case ROOMTYPE_FAMILY_ROOM:
      return LOCALE_COMMON_PATHS.ROOM_TYPE_FAMILY_ROOM;
    case ROOMTYPE_GAME_ROOM:
      return LOCALE_COMMON_PATHS.ROOM_TYPE_GAME_ROOM;
    case ROOMTYPE_GREAT_ROOM:
      return LOCALE_COMMON_PATHS.ROOM_TYPE_GREAT_ROOM;
    case ROOMTYPE_GYM:
      return LOCALE_COMMON_PATHS.ROOM_TYPE_GYM;
    case ROOMTYPE_KITCHEN:
      return LOCALE_COMMON_PATHS.ROOM_TYPE_KITCHEN;
    case ROOMTYPE_LAUNDRY:
      return LOCALE_COMMON_PATHS.ROOM_TYPE_LAUNDRY;
    case ROOMTYPE_LIBRARY:
      return LOCALE_COMMON_PATHS.ROOM_TYPE_LIBRARY;
    case ROOMTYPE_LIVING_ROOM:
      return LOCALE_COMMON_PATHS.ROOM_TYPE_LIVING_ROOM;
    case ROOMTYPE_LOFT:
      return LOCALE_COMMON_PATHS.ROOM_TYPE_LOFT;
    case ROOMTYPE_MASTER_BATHROOM:
      return LOCALE_COMMON_PATHS.ROOM_TYPE_MASTER_BATHROOM;
    case ROOMTYPE_MASTER_BEDROOM:
      return LOCALE_COMMON_PATHS.ROOM_TYPE_MASTER_BEDROOM;
    case ROOMTYPE_MEDIA_ROOM:
      return LOCALE_COMMON_PATHS.ROOM_TYPE_MEDIA_ROOM;
    case ROOMTYPE_OFFICE:
      return LOCALE_COMMON_PATHS.ROOM_TYPE_OFFICE;
    case ROOMTYPE_SAUNA:
      return LOCALE_COMMON_PATHS.ROOM_TYPE_SAUNA;
    case ROOMTYPE_UTILITY_ROOM:
      return LOCALE_COMMON_PATHS.ROOM_TYPE_UTILITY_ROOM;
    case ROOMTYPE_WORKSHOP:
      return LOCALE_COMMON_PATHS.ROOM_TYPE_WORKSHOP;
    default:
      return roomType;
  }
}

//#endregion

//#region Room Level related constants and helper functions

export const ROOMLEVEL_BASEMENT = 'Basement' as const;
export const ROOMLEVEL_FIRST = 'First' as const;
export const ROOMLEVEL_LOWER = 'Lower' as const;
export const ROOMLEVEL_MAIN = 'Main' as const;
export const ROOMLEVEL_SECOND = 'Second' as const;
export const ROOMLEVEL_THIRD = 'Third' as const;
export const ROOMLEVEL_UPPER = 'Upper' as const;

/** The level within the dwelling on which the given room is located. */
export type RoomLevel = typeof ROOMLEVEL_BASEMENT | typeof ROOMLEVEL_FIRST | typeof ROOMLEVEL_LOWER | typeof ROOMLEVEL_MAIN | typeof ROOMLEVEL_SECOND | typeof ROOMLEVEL_THIRD | typeof ROOMLEVEL_UPPER;

export function getRoomLevelLabelKey(roomLevel: RoomLevel) {
  switch (roomLevel) {
    case ROOMLEVEL_BASEMENT:
      return LOCALE_COMMON_PATHS.ROOM_LEVEL_BASEMENT;
    case ROOMLEVEL_FIRST:
      return LOCALE_COMMON_PATHS.ROOM_LEVEL_FIRST;
    case ROOMLEVEL_LOWER:
      return LOCALE_COMMON_PATHS.ROOM_LEVEL_LOWER;
    case ROOMLEVEL_MAIN:
      return LOCALE_COMMON_PATHS.ROOM_LEVEL_MAIN;
    case ROOMLEVEL_SECOND:
      return LOCALE_COMMON_PATHS.ROOM_LEVEL_SECOND;
    case ROOMLEVEL_THIRD:
      return LOCALE_COMMON_PATHS.ROOM_LEVEL_THIRD;
    case ROOMLEVEL_UPPER:
      return LOCALE_COMMON_PATHS.ROOM_LEVEL_UPPER;
    default:
      return roomLevel;
  }
}

//#endregion

//#region SEO Property Type Label Keys

export const SEO_TYPE_HOUSE = 'House' as const;
export const SEO_TYPE_CONDO = 'Condo' as const;
export const SEO_TYPE_TOWNHOUSE = 'Townhouse' as const;

/** SEO-friendly property types derived from listing.seoType (for display in headings) */
export type SeoPropertyType = typeof SEO_TYPE_HOUSE | typeof SEO_TYPE_CONDO | typeof SEO_TYPE_TOWNHOUSE;

/**
 * Gets the translation key for a SEO property type from listing.seoType.
 * This is separate from RESO PropertyType and is used for display-friendly property types.
 * 
 * @param seoType - The property type from listing.seoType (e.g., 'House', 'Condo', 'Townhouse')
 * @returns The translation key from LOCALE_COMMON_PATHS
 * 
 * @example
 * const key = getSeoPropertyTypeLabelKey(listing.seoType);
 * const translated = t(key, { defaultValue: listing.seoType || 'Real Estate' });
 */
export function getSeoPropertyTypeLabelKey(seoType: string | null | undefined): string {
  switch (seoType) {
    case SEO_TYPE_HOUSE:
      return LOCALE_COMMON_PATHS.ADDRESS_HEADING_SEO_TYPE_HOUSE;
    case SEO_TYPE_CONDO:
      return LOCALE_COMMON_PATHS.ADDRESS_HEADING_SEO_TYPE_CONDO;
    case SEO_TYPE_TOWNHOUSE:
      return LOCALE_COMMON_PATHS.ADDRESS_HEADING_SEO_TYPE_TOWNHOUSE;
    default:
      // Fallback to the unknown property type key for null/undefined or unrecognized types
      return LOCALE_COMMON_PATHS.ADDRESS_HEADING_PROPERTY_TYPE_UNKNOWN;
  }
}

//#endregion